import React, { useState, useEffect } from 'react';
import versionManager from '../utils/versionManager.js';
import '../styles/AppUpdatePrompt.css';

const AppUpdatePrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateProgress, setUpdateProgress] = useState(0);

  useEffect(() => {
    const checkForUpdates = () => {
      const info = versionManager.getVersionInfo();
      setVersionInfo(info);

      // Only show if update is needed
      if (!info.needsUpdate) {
        return;
      }

      // Check if prompt was dismissed
      const promptDismissed = localStorage.getItem('update-prompt-dismissed');
      if (promptDismissed) {
        const dismissedTime = parseInt(promptDismissed);
        const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);

        // Show again after 24 hours
        if (hoursSinceDismissed < 24) {
          return;
        }
      }

      // Show prompt after 5 seconds to not be too intrusive
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    };

    checkForUpdates();
  }, []);

  const handleUpdateClick = async () => {
    setIsUpdating(true);
    setUpdateProgress(0);

    try {
      // Simulate update progress
      const progressSteps = [15, 35, 55, 75, 95, 100];

      for (const step of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, 300));
        setUpdateProgress(step);
      }

      // Perform actual update
      await versionManager.updateVersion();

      // Success - hide prompt and refresh
      setShowPrompt(false);
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Update failed:', error);
      setIsUpdating(false);
      setUpdateProgress(0);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('update-prompt-dismissed', Date.now().toString());
  };

  // Don't show if no update needed or prompt dismissed
  if (!versionInfo?.needsUpdate || !showPrompt) {
    return null;
  }

  return (
    <div className="app-update-prompt">
      <div className="app-update-content">
        <div className="app-update-icon">🚀</div>
        <div className="app-update-text">
          <h3>Update Available</h3>
          <p>New version v{versionManager.getCurrentVersion()} is ready!</p>

          {isUpdating && (
            <div className="update-progress-inline">
              <div className="progress-bar-inline">
                <div
                  className="progress-fill-inline"
                  style={{ width: `${updateProgress}%` }}
                ></div>
              </div>
              <span className="progress-text-inline">Updating... {updateProgress}%</span>
            </div>
          )}
        </div>

        {!isUpdating && (
          <div className="app-update-buttons">
            <button
              className="update-button-prompt"
              onClick={handleUpdateClick}
            >
              Update Now
            </button>
            <button
              className="dismiss-button-prompt"
              onClick={handleDismiss}
            >
              Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppUpdatePrompt;